import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Heart, Music, CheckCircle, Upload, X, FileImage, AlertCircle, ArrowRight, Gift, Star, Sparkles, Flame, AlertTriangle, Play } from 'lucide-react';
import CorporateHeader from './CorporateHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FLYER_IMAGE = 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/2gywasiv_FINAL%20visual%20for%20digital%20LUXURY%20FRAMES.png';
const SONG_COVER = 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/9g5pun4v_NEW%20SINGLE%20COVER%20ART%20-%20FINAL.png';
const YOUTUBE_EMBED = 'https://www.youtube.com/embed/ggELJ3HPaQ0?rel=0&modestbranding=1';

const STRIPE_LINKS = {
  frame_1: 'https://buy.stripe.com/7sY6oJ2NY9wEbzudV6ebu01',
  frame_2: 'https://buy.stripe.com/9B68wRbkucIQcDybMYebu02',
  frame_3: 'https://buy.stripe.com/bJeeVfagq24c1YU5oAebu03',
  music_single: 'https://buy.stripe.com/cNibJ3dsC1081YUdV6ebu04',
  bundle: 'https://buy.stripe.com/9B6aEZ74e7ow6fa18kebu05',
};

// Friendly labels for package types in social-proof ticker
const PKG_LABEL = {
  '1 Person ($25)': '1-Person tribute frame',
  '2 People ($30)': 'Mother & Child frame',
  '3 People ($35)': 'Family tribute frame',
  'Music Only ($4.99)': '"My Momma Is My Hero" song',
  'Bundle ($27.99)': 'Frame + Song Bundle',
};

// Map URL param `pkg` → dropdown value
const PKG_PARAM_MAP = {
  '1person': '1 Person ($25)',
  '2people': '2 People ($30)',
  '3people': '3 People ($35)',
  'music': 'Music Only ($4.99)',
  'bundle': 'Bundle ($27.99)',
};

const SocialProofTicker = () => {
  const [messages, setMessages] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let mounted = true;
    fetch(`${API_URL}/api/mothers-day-frame/social-proof`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!mounted || !data) return;
        const msgs = [];
        if (data.today > 0) msgs.push({ icon: 'flame', text: `${data.today} ${data.today === 1 ? 'frame' : 'frames'} ordered today` });
        if (data.last_24h > 0) msgs.push({ icon: 'sparkles', text: `${data.last_24h} ${data.last_24h === 1 ? 'order' : 'orders'} in the last 24 hours` });
        (data.recent_packages || []).slice(0, 3).forEach(p => {
          const label = PKG_LABEL[p];
          if (label) msgs.push({ icon: 'heart', text: `Someone just ordered a ${label}` });
        });
        if (data.total > 5) msgs.push({ icon: 'sparkles', text: `Join ${data.total}+ families honoring their mothers` });
        if (msgs.length > 0) setMessages(msgs);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (messages.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % messages.length), 4000);
    return () => clearInterval(t);
  }, [messages.length]);

  if (messages.length === 0) return null;
  const current = messages[idx];
  const Icon = current.icon === 'flame' ? Flame : current.icon === 'heart' ? Heart : Sparkles;

  return (
    <div className="w-full bg-gradient-to-r from-pink-900/40 via-amber-900/30 to-pink-900/40 border-b border-amber-700/30 backdrop-blur-sm" data-testid="social-proof-ticker">
      <div className="container mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-center">
        <Icon className="w-4 h-4 text-amber-400 flex-shrink-0 animate-pulse" />
        <p
          key={idx}
          className="text-xs sm:text-sm font-medium text-amber-100 truncate animate-[fadeIn_0.5s_ease-in]"
          data-testid="social-proof-message"
          style={{ animation: 'fadeIn 0.5s ease-in' }}
        >
          {current.text}
        </p>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

// Post-purchase confirmation banner (shows when URL has ?pkg=... or #order-form)
const PurchaseBanner = ({ purchasedPkg }) => {
  if (!purchasedPkg) return null;
  const isBundle = purchasedPkg === 'Bundle ($27.99)';
  return (
    <div className="bg-gradient-to-r from-green-900/40 via-pink-900/40 to-amber-900/40 border-y-2 border-amber-400/50" data-testid="purchase-banner">
      <div className="container mx-auto px-4 py-5 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
          <h2 className="text-xl sm:text-2xl font-bold text-white" data-testid="purchase-banner-title">
            Payment Confirmed. Your transformation starts now.
          </h2>
        </div>
        {isBundle && (
          <p className="text-amber-300 text-sm sm:text-base mt-2">
            You purchased the <span className="font-bold">BUNDLE</span> — your song is ready & your custom frame is next.
          </p>
        )}
      </div>
    </div>
  );
};

// Bundle-specific dual-section shown right after banner when bundle was purchased
const BundleReadySection = ({ onScrollToForm }) => (
  <section className="py-10 border-b border-gray-800/50 bg-gradient-to-b from-gray-950 to-black" data-testid="bundle-ready-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Section 1: Song ready */}
        <div className="bg-gray-900/70 border border-amber-600/40 rounded-2xl p-6 text-center">
          <Music className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h3 className="text-white font-bold text-xl mb-2" data-testid="bundle-song-ready-title">Your Song is Ready</h3>
          <p className="text-gray-300 text-sm mb-4">
            Check your email for your full download of<br />
            <span className="text-amber-300 italic">"My Momma Is My Hero"</span>
          </p>
          <p className="text-gray-500 text-xs">Delivery within minutes. Check spam folder if needed.</p>
        </div>
        {/* Section 2: Complete frame */}
        <div className="bg-gray-900/70 border border-pink-600/40 rounded-2xl p-6 text-center">
          <Heart className="w-10 h-10 text-pink-400 mx-auto mb-3" />
          <h3 className="text-white font-bold text-xl mb-2" data-testid="bundle-frame-next-title">Complete Your Custom Frame Below</h3>
          <p className="text-gray-300 text-sm mb-4">
            Scroll down to upload your photos so we can design your custom digital frame.
          </p>
          <Button
            onClick={onScrollToForm}
            className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white font-bold px-6 py-3 rounded-full"
            data-testid="bundle-scroll-to-form"
          >
            Go to Photo Upload
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  </section>
);

const MothersDayFramesPage = () => {
  const formRef = useRef(null);
  const [form, setForm] = useState({
    fullName: '', email: '', socialName: '', packagePurchased: '',
    whoInPhotos: '', namesOnFrame: '', specialMessage: '', confirmPurchase: false,
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [purchasedPkg, setPurchasedPkg] = useState('');

  // Detect post-purchase return (via ?pkg=... query or #order-form hash)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pkgParam = params.get('pkg');
    const matched = pkgParam && PKG_PARAM_MAP[pkgParam.toLowerCase()];
    if (matched) {
      setPurchasedPkg(matched);
      setForm(prev => ({ ...prev, packagePurchased: matched }));
    }
    // Auto-scroll to form when hash #order-form present (e.g., Stripe success_url)
    if (window.location.hash === '#order-form') {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }
  }, []);

  const handleInput = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024);
    if (valid.length !== files.length) setError('Some files rejected. Images under 10MB only.');
    setPhotos(prev => [...prev, ...valid].slice(0, 5));
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth' });

  const isMusicOnly = form.packagePurchased === 'Music Only ($4.99)';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fullName || !form.email) { setError('Name and email are required.'); return; }
    if (!form.packagePurchased) { setError('Please select which package you purchased.'); return; }
    if (!form.confirmPurchase) { setError('Please confirm you selected the correct package.'); return; }
    if (!isMusicOnly && photos.length === 0) {
      setError('Please upload at least one photo for your frame order.'); return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('fullName', form.fullName);
      formData.append('email', form.email);
      formData.append('socialName', form.socialName);
      formData.append('packagePurchased', form.packagePurchased);
      formData.append('whoInPhotos', form.whoInPhotos);
      formData.append('namesOnFrame', form.namesOnFrame);
      formData.append('specialMessage', form.specialMessage);
      photos.forEach(p => formData.append('photos', p));

      const res = await fetch(`${API_URL}/api/mothers-day-frame/intake`, { method: 'POST', body: formData });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Submission failed'); }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black">
        <CorporateHeader />
        <div className="pt-28 pb-20">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <div className="bg-gray-900/50 border border-pink-800/30 rounded-2xl p-8 sm:p-12">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-4" data-testid="frame-order-confirmed">Your Order Has Been Received</h1>
              <p className="text-pink-400 font-semibold text-lg mb-4">Your custom frame will be delivered within 24-48 hours.</p>
              <p className="text-gray-400 mb-4">Check your email for a confirmation.</p>
              {(isMusicOnly || form.packagePurchased === 'Bundle ($27.99)') && (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded-xl p-4 mt-4 mb-6">
                  <p className="text-amber-300 font-semibold text-sm flex items-center justify-center gap-2">
                    <Music className="w-4 h-4" />
                    Your song download has been sent to your email.
                  </p>
                </div>
              )}
              <div className="mt-6">
                <a href="/" className="text-amber-400 hover:text-amber-300 font-medium">Return Home</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* Top pinned ticker */}
      <div className="pt-20">
        <SocialProofTicker />
      </div>

      {/* Post-purchase confirmation banner (shows when ?pkg=... URL param present) */}
      <PurchaseBanner purchasedPkg={purchasedPkg} />

      {/* Bundle-specific dual section */}
      {purchasedPkg === 'Bundle ($27.99)' && <BundleReadySection onScrollToForm={scrollToForm} />}

      {/* ===== SECTION 1: HERO ===== */}
      <section className="pt-8 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-900/10 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" data-testid="frames-hero-title">
              MOTHER'S DAY<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-amber-400 to-pink-400">DIGITAL PROFILE FRAMES</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 italic mb-4" style={{fontFamily: 'Georgia, serif'}}>
              Honor Her. Celebrate Her. Keep Her Legacy Alive.
            </p>
            <div className="inline-block bg-pink-900/30 border border-pink-600/40 rounded-full px-6 py-2 mt-2">
              <p className="text-pink-300 text-sm font-bold tracking-wider">
                NOT PHYSICAL PRODUCTS &bull; 100% DIGITAL &bull; MADE FOR FACEBOOK
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="relative max-w-3xl w-full">
              <div className="absolute -inset-4 bg-gradient-to-r from-pink-600/15 via-amber-600/15 to-pink-600/15 rounded-3xl blur-2xl" />
              <img src={FLYER_IMAGE} alt="Mother's Day Digital Profile Frames" className="relative w-full rounded-2xl shadow-2xl border border-amber-800/20" data-testid="frames-flyer-image" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: PRICING ===== */}
      <section className="py-16 border-t border-gray-800/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">Choose Your Package</h2>
          <p className="text-gray-400 text-center mb-12">Select your option below to purchase</p>

          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {/* CARD 1: CUSTOM FRAME — 3 separate buy buttons */}
            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-8 text-center hover:border-pink-600/50 transition-all flex flex-col">
              <Heart className="w-10 h-10 text-pink-400 mx-auto mb-4" />
              <h3 className="text-white font-bold text-2xl mb-6">CUSTOM FRAME</h3>
              <div className="space-y-3 mb-6 text-left px-2">
                <p className="flex justify-between items-baseline"><span className="text-white font-bold text-xl">1 PERSON</span><span className="text-pink-400 font-bold text-2xl">$25</span></p>
                <p className="flex justify-between items-baseline"><span className="text-white font-bold text-xl">2 PEOPLE</span><span className="text-pink-400 font-bold text-2xl">$30</span></p>
                <p className="flex justify-between items-baseline"><span className="text-white font-bold text-xl">3 PEOPLE</span><span className="text-pink-400 font-bold text-2xl">$35</span></p>
              </div>
              <p className="text-gray-300 text-sm italic mb-4">Photos do NOT need to be together — we merge them.</p>
              <p className="text-pink-400 text-sm font-semibold mb-5">Perfect for Mothers, Grandmothers & Memorial Tributes</p>
              <div className="mt-auto space-y-2.5">
                <a href={STRIPE_LINKS.frame_1} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white font-bold py-5 text-sm sm:text-base rounded-full min-h-[50px]" data-testid="buy-frame-1">
                    Buy 1 Person Frame — $25
                  </Button>
                </a>
                <a href={STRIPE_LINKS.frame_2} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white font-bold py-5 text-sm sm:text-base rounded-full min-h-[50px]" data-testid="buy-frame-2">
                    Buy 2 Person Frame — $30
                  </Button>
                </a>
                <a href={STRIPE_LINKS.frame_3} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white font-bold py-5 text-sm sm:text-base rounded-full min-h-[50px]" data-testid="buy-frame-3">
                    Buy 3 Person Frame — $35
                  </Button>
                </a>
              </div>
            </div>

            {/* CARD 2: MUSIC SINGLE */}
            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-8 text-center hover:border-amber-600/50 transition-all flex flex-col">
              <Music className="w-10 h-10 text-amber-400 mx-auto mb-4" />
              <h3 className="text-white font-bold text-xl mb-2">MUSIC SINGLE</h3>
              <p className="text-3xl font-bold text-white mb-2">$4.99</p>
              <p className="text-gray-400 text-sm mb-3 italic">"My Momma Is My Hero"</p>
              <p className="text-gray-500 text-xs mb-2">The official tribute song</p>
              <p className="text-gray-500 text-xs mb-6">Use on Reels, TikTok, YouTube & FB</p>
              <div className="mt-auto">
                <a href={STRIPE_LINKS.music_single} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold py-6 text-base rounded-full min-h-[52px]" data-testid="buy-music-single">
                    Buy Music Single — $4.99
                  </Button>
                </a>
              </div>
            </div>

            {/* CARD 3: BUNDLE */}
            <div className="bg-gray-900/60 border-2 border-amber-500/50 rounded-2xl p-8 text-center relative shadow-lg shadow-amber-900/20 flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black text-xs font-bold px-4 py-1 rounded-full">BEST VALUE</div>
              <Gift className="w-10 h-10 text-amber-400 mx-auto mb-4" />
              <h3 className="text-white font-bold text-xl mb-2">BUNDLE</h3>
              <p className="text-3xl font-bold text-white mb-1">$27.99</p>
              <p className="text-green-400 text-sm font-bold mb-3">YOU SAVE $2.00</p>
              <p className="text-gray-300 text-sm mb-2">Custom Frame + Tribute Song</p>
              <p className="text-gray-500 text-xs mb-6 italic">Best value for honoring her legacy</p>
              <div className="mt-auto">
                <a href={STRIPE_LINKS.bundle} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold py-6 text-base rounded-full min-h-[52px]" data-testid="buy-bundle">
                    Buy Bundle — $27.99
                  </Button>
                </a>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">After purchasing, scroll down to complete your order form.</p>
        </div>
      </section>

      {/* ===== SECTION 3: WHAT YOU'RE GETTING ===== */}
      <section className="py-16 border-t border-gray-800/50 bg-gray-950/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">What You're Getting</h2>
          <p className="text-gray-300 text-lg mb-8 leading-relaxed">
            A custom digital Facebook profile frame designed using your personal photos.<br />
            Perfect for honoring mothers, grandmothers, or loved ones in heaven.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto text-left">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-gray-200">High-quality design</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-gray-200">Personalized names</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-gray-200">Memorial & tribute options</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-gray-200">Delivered in 24-48 hours</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: HOW IT WORKS ===== */}
      <section className="py-20 border-t border-gray-800/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <h2 className="text-4xl sm:text-5xl font-bold text-white text-center mb-16">How It Works</h2>
          <div className="space-y-10 sm:space-y-0 sm:grid sm:grid-cols-5 sm:gap-8">
            {[
              { step: '1', title: 'Choose Your Package', desc: '' },
              { step: '2', title: 'Complete Payment', desc: '' },
              { step: '3', title: 'Upload Your Photos', desc: '' },
              { step: '4', title: 'We Design Your Frame', desc: '' },
              { step: '5', title: 'You Receive It', desc: '24–48 hours' },
            ].map((s, i) => (
              <div key={i} className="text-center flex sm:flex-col items-center sm:items-center gap-5 sm:gap-0">
                <div className="w-20 h-20 bg-pink-600/20 border-2 border-pink-600/40 rounded-full flex items-center justify-center flex-shrink-0 sm:mx-auto sm:mb-5">
                  <span className="text-pink-400 font-bold text-3xl">{s.step}</span>
                </div>
                <div className="text-left sm:text-center">
                  <h3 className="text-white font-bold text-xl sm:text-2xl mb-2 leading-tight">{s.title}</h3>
                  {s.desc && <p className="text-amber-400 text-base sm:text-lg font-semibold">{s.desc}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SCROLL CTA above form ===== */}
      <section className="py-8 border-t border-gray-800/50 bg-gradient-to-r from-pink-900/10 via-transparent to-pink-900/10">
        <div className="container mx-auto px-4 text-center">
          <Button
            onClick={scrollToForm}
            className="bg-gradient-to-r from-pink-600 to-amber-600 hover:from-pink-700 hover:to-amber-700 text-white font-bold px-8 py-5 text-base sm:text-lg rounded-full shadow-lg shadow-pink-900/30"
            data-testid="scroll-cta-upload-photos"
          >
            <Upload className="w-5 h-5 mr-2" />
            Upload Your Photos & Complete Order
          </Button>
        </div>
      </section>

      {/* ===== SECTION 5: INTAKE FORM (highlighted & mandatory) ===== */}
      <section ref={formRef} className="py-16 border-t border-gray-800/50 bg-gray-950" id="order-form">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          {/* Step indicator */}
          <p className="text-amber-400 text-sm sm:text-base font-bold tracking-widest uppercase text-center mb-3" data-testid="form-step-indicator">
            Step 2 of 2: Submit Your Photos
          </p>
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 flex items-center justify-center gap-2 flex-wrap" data-testid="form-section-title">
              <AlertTriangle className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400" />
              IMPORTANT: Complete Your Order Now
            </h2>
            <p className="text-pink-300 font-bold text-base sm:text-lg max-w-xl mx-auto" data-testid="form-section-subtext">
              Your order is NOT complete until you submit your photos and details below.
            </p>
          </div>

          {/* Highlighted form wrapper (glow + border) */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-pink-600/30 via-amber-500/20 to-pink-600/30 rounded-2xl blur-md" />
            <form
              onSubmit={handleSubmit}
              className="relative space-y-6 bg-gray-900/80 border-2 border-pink-600/40 rounded-2xl p-5 sm:p-8"
              data-testid="intake-form"
            >
              {/* Contact Info */}
              <div className="bg-black/40 border border-gray-800 rounded-xl p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name <span className="text-pink-400">*</span></label>
                  <Input value={form.fullName} onChange={(e) => handleInput('fullName', e.target.value)} placeholder="Your full name" required className="bg-black border-gray-700 text-white" data-testid="frame-fullName" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Email Address <span className="text-pink-400">*</span></label>
                  <Input type="email" value={form.email} onChange={(e) => handleInput('email', e.target.value)} placeholder="your@email.com" required className="bg-black border-gray-700 text-white" data-testid="frame-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Social Media Handle <span className="text-gray-600">(optional)</span></label>
                  <Input value={form.socialName} onChange={(e) => handleInput('socialName', e.target.value)} placeholder="@yourname" className="bg-black border-gray-700 text-white" data-testid="frame-socialName" />
                </div>
              </div>

              {/* Package Selection */}
              <div className="bg-black/40 border border-gray-800 rounded-xl p-5">
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Package Selected <span className="text-pink-400">*</span></label>
                <select value={form.packagePurchased} onChange={(e) => handleInput('packagePurchased', e.target.value)} required className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-pink-500 focus:border-pink-500 appearance-none" data-testid="frame-package">
                  <option value="" disabled>Select your package...</option>
                  <option value="1 Person ($25)">1 Person Frame ($25)</option>
                  <option value="2 People ($30)">2 Person Frame ($30)</option>
                  <option value="3 People ($35)">3 Person Frame ($35)</option>
                  <option value="Music Only ($4.99)">Music Single ($4.99)</option>
                  <option value="Bundle ($27.99)">Bundle ($27.99)</option>
                </select>
              </div>

              {/* Photo Upload (hidden for Music Only) */}
              {!isMusicOnly && (
                <div className="bg-black/40 border border-gray-800 rounded-xl p-5">
                  <h3 className="text-lg font-bold text-white mb-1">Upload Your Photos <span className="text-pink-400">*</span></h3>
                  <p className="text-gray-500 text-sm mb-4">Upload up to 5 photos (JPG, PNG — Max 10MB each)</p>

                  {photos.length < 5 && (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 hover:border-pink-600/50 rounded-xl p-8 cursor-pointer transition-colors mb-4" data-testid="frame-photo-upload">
                      <Upload className="w-10 h-10 text-gray-500 mb-3" />
                      <span className="text-gray-400 text-sm font-semibold">Click to upload photos</span>
                      <span className="text-gray-600 text-xs mt-1">Old, new, or both — we merge them</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" multiple />
                    </label>
                  )}

                  {photos.length > 0 && (
                    <div className="flex gap-3 flex-wrap">
                      {photos.map((p, i) => (
                        <div key={i} className="relative bg-gray-800 rounded-xl p-3 flex items-center gap-3">
                          <FileImage className="w-6 h-6 text-pink-400" />
                          <p className="text-white text-sm truncate max-w-[140px]">{p.name}</p>
                          <button type="button" onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1"><X className="w-3 h-3 text-white" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Frame Details (hidden for Music Only) */}
              {!isMusicOnly && (
                <div className="bg-black/40 border border-gray-800 rounded-xl p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Who is in each photo?</label>
                    <textarea value={form.whoInPhotos} onChange={(e) => handleInput('whoInPhotos', e.target.value)} placeholder="e.g., Photo 1: Mom and Grandma, Photo 2: The whole family..." rows={3} className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-pink-500 focus:border-pink-500" data-testid="frame-whoInPhotos" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Names to display on frame</label>
                    <Input value={form.namesOnFrame} onChange={(e) => handleInput('namesOnFrame', e.target.value)} placeholder="e.g., Gloria, Audrey, Family Name..." className="bg-black border-gray-700 text-white" data-testid="frame-namesOnFrame" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Special message <span className="text-gray-600">(optional)</span></label>
                    <textarea value={form.specialMessage} onChange={(e) => handleInput('specialMessage', e.target.value)} placeholder="e.g., In loving memory of... / Happy Mother's Day..." rows={3} className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-pink-500 focus:border-pink-500" data-testid="frame-specialMessage" />
                  </div>
                </div>
              )}

              {/* Confirm */}
              <div className="bg-black/40 border border-gray-800 rounded-xl p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.confirmPurchase} onChange={(e) => handleInput('confirmPurchase', e.target.checked)} className="mt-1 accent-pink-500 w-5 h-5" data-testid="frame-confirmPurchase" />
                  <span className="text-gray-300 text-sm">I confirm I selected the correct package <span className="text-pink-400">*</span></span>
                </label>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-900/20 border border-red-800 text-red-400 rounded-xl p-4 text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
                </div>
              )}

              {/* Mandatory photo warning (frame/bundle only) */}
              {!isMusicOnly && (
                <div className="bg-amber-900/20 border border-amber-600/40 rounded-xl p-4 text-center" data-testid="orders-no-photos-warning">
                  <p className="text-amber-300 font-bold text-sm sm:text-base flex items-center justify-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Orders without photos cannot be completed.
                  </p>
                </div>
              )}

              <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-pink-600 to-amber-600 hover:from-pink-700 hover:to-amber-700 text-white font-bold py-5 text-base sm:text-lg rounded-full shadow-lg shadow-pink-900/40" data-testid="frame-submit">
                {submitting ? 'Submitting...' : 'Submit My Photos & Complete Order'}
                {!submitting && <ArrowRight className="w-5 h-5 ml-2" />}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: TRUST MARKS ===== */}
      <section className="py-12 border-t border-gray-800/50">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3">
              <CheckCircle className="w-5 h-5 text-pink-400" />
              <span className="text-gray-300">Delivered within 24-48 hours</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Star className="w-5 h-5 text-amber-400" />
              <span className="text-gray-300">High quality luxury design</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Heart className="w-5 h-5 text-pink-400" />
              <span className="text-gray-300">Made with love to honor her legacy</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: MUSIC (BELOW intake form, reduced dominance) ===== */}
      <section className="py-12 border-t border-gray-800/50 bg-gray-950/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="text-center mb-6">
            <p className="text-amber-400 text-xs font-bold tracking-widest uppercase mb-2">Preview the Song</p>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1" data-testid="music-section-title">"My Momma Is My Hero"</h2>
            <p className="text-gray-400 text-sm italic">A tribute by Egypt Young</p>
          </div>

          {/* Responsive YouTube embed (16:9) */}
          <div className="relative w-full rounded-xl overflow-hidden border border-amber-700/30 shadow-lg shadow-amber-900/10" style={{ paddingTop: '56.25%' }} data-testid="youtube-embed-wrapper">
            <iframe
              className="absolute inset-0 w-full h-full"
              src={YOUTUBE_EMBED}
              title="My Momma Is My Hero — Preview"
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-embed"
            />
          </div>

          <div className="text-center mt-6">
            <a href={STRIPE_LINKS.music_single} target="_blank" rel="noopener noreferrer">
              <Button
                className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold px-8 py-4 text-base rounded-full min-h-[48px]"
                data-testid="buy-song-below-video"
              >
                <Play className="w-4 h-4 mr-2 fill-current" />
                Buy Song — $4.99
              </Button>
            </a>
            <p className="text-gray-500 text-xs mt-3" data-testid="music-subtext">
              Watch the video above &bull; Full download available after purchase
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="py-8 border-t border-gray-800/50 text-center">
        <p className="text-amber-400/80 text-sm font-semibold tracking-wider">eBlackBookListings</p>
        <p className="text-gray-600 text-xs mt-1">100% Digital &bull; Premium Quality &bull; Custom Names &bull; Fast Delivery</p>
      </div>
    </div>
  );
};

export default MothersDayFramesPage;
